import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  input,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { NavigationEnd, PRIMARY_OUTLET, Router, UrlTree } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SelectOption, SelectOptionValue } from 'form-field';
import { lang } from 'interfaces';
import { LanguageService } from 'language';
import { TranslatePipe } from 'translate';
import { MenuItemComponent, MenuItemLink } from '../menu-item/menu-item.component';
import { MenuComponent } from '../menu.component';

@Component({
  selector: 'lib-language-selector',
  templateUrl: './language-selector.component.html',
  styleUrls: ['./language-selector.component.scss'],
  imports: [MenuItemComponent, MenuComponent, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LanguageSelectorComponent implements OnInit, OnDestroy {
  public lang = inject(LanguageService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  @Input() public collapseOnMobile = false;
  @Input() public lightTheme = false;
  private languageChangeSubscription = Subscription.EMPTY;
  public currentUrl: UrlTree;
  public filteredLangs: SelectOption[] = [];
  public buttonClass = input('');
  public textClass = input('');

  public get languageAgnosticPath(): string[] {
    if (this.currentUrl?.root.hasChildren()) {
      return this.currentUrl.root.children[PRIMARY_OUTLET].segments
        .map(s => s.path)
        .filter(p => p !== this.lang.current);
    }
    return ['/'];
  }

  constructor() {
    this.initLang();
  }

  public initLang() {
    this.languageChangeSubscription = this.lang.onLangChange.subscribe(() => {
      this.lang.setLanguage(this.lang.current);
      this.setFilteredLangs();
      this.cdr.markForCheck();
    });
  }

  public ngOnInit() {
    this.currentUrl = this.router.parseUrl(this.router.url);

    this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(e => {
      this.currentUrl = this.router.parseUrl((e as NavigationEnd).urlAfterRedirects);
    });
  }

  public ngOnDestroy() {
    this.languageChangeSubscription.unsubscribe();
  }

  public getPath(lang: lang): string[] {
    return ['/', lang, ...this.languageAgnosticPath];
  }

  public getMenuItemLink(lang: SelectOptionValue): MenuItemLink {
    return {
      routerLink: this.getPath(lang as lang),
      queryParams: this.currentUrl?.queryParams,
      queryParamsHandling: 'merge',
      fragment: this.currentUrl?.fragment,
    };
  }

  private setFilteredLangs() {
    this.filteredLangs = this.lang.langs
      .filter(lang => lang !== this.lang.current)
      .map(lang => ({
        value: lang,
        label: this.lang.langsFull[lang],
      }));
  }
}
